import {capitalizeFirst} from '@wandb/weave/core';

import {
  type ChartAggOption,
  type SmoothingType,
  XAxisFormatValues,
} from '../../../generated/graphql';
import type {PanelLayout, PanelWithConfig} from '../../../util/panelTypes';
import {
  type ConfigValueForRun,
  type ConfigValuesNumberAggregation,
} from './semanticLegend/utils/useConfigValuesAggregation';

// All fields in the type should exist, but can be undefined
export type Complete<T> = {
  [P in keyof Required<T>]: Pick<T, P> extends Required<Pick<T, P>>
    ? T[P]
    : T[P] | undefined;
};

/**
 * PanelSettings are settings that apply to a _panel_; not necessarily any
 * one specific panel, but panels in general. They may be used to define
 * common settings for a group of panels, such as all panels in a workspace
 * or section of a workspace.
 */
export interface Settings {
  /** Whether run names should be colored in panel tooltips */
  colorRunNames?: boolean;
  /** "no crop" option for chart legends on the primary chart to accommodate long run names with crucial information in the middle */
  displayFullRunName?: boolean;
  excludeOutliers?: ExcludeOutliersValues;
  localSmoothingSettingsActive?: number;
  localxAxisSettingsActive?: number;
  /** Workspace setting level uses `maxRuns`, but panel config uses `limit`. This setting tells us maximum number of runs to include for a panel */
  maxRuns?: number;
  pointVisualizationMethod?: PointVisualizationOptions; // Default point vizualization method for runs line plots
  showLegend?: boolean;
  smoothingActive?: boolean;
  smoothingType?: SmoothingType;
  smoothingWeight?: number;
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions; // Determines how many runs are displayed in panel tooltips
  useRunsTableGroupingInPanels?: boolean;
  xAxis?: XAxisValues | string;
  xAxisFormat?: XAxisFormatValues;
  xAxisActive?: boolean;
  xAxisMax?: number;
  xAxisMin?: number;
}

export const xAxisFormatOptions = [
  {
    label: capitalizeFirst(XAxisFormatValues.Number),
    value: XAxisFormatValues.Number,
  },
  {
    label: capitalizeFirst(XAxisFormatValues.Datetime),
    value: XAxisFormatValues.Datetime,
  },
] as const;

/**
 * All possible setting levels.
 */
export type SettingLevel = 'workspace' | 'section' | 'panel' | 'report';

export interface SmoothingSettings {
  smoothingWeight: number;
  smoothingType: SmoothingType;
}

export enum TooltipNumberOfRunsOptions {
  Single = 'single',
  Default = 'default',
  All = 'all_runs',
}

export enum OrganizationPrefix {
  FirstPrefix = 1,
  LastPrefix,
}

/**
 * Indicates which menu was selected in the Settings UX
 */
export type SelectedSettingType =
  | 'line-plot'
  | 'workspace-layout'
  | 'semantic-legend'
  | 'media';

export type YAxisType = 'linear' | 'log';
export type XAxisType = 'linear' | 'log' | 'time';

export enum XAxisValues {
  Step = '_step',
  RunTime = '_runtime',
  Timestamp = '_timestamp',
  AbsoluteRunTime = '_absolute_runtime',
}

export enum YAxisValues {
  Min = 'min',
  Max = 'max',
  Last = 'last',
  Config_Number = 'config_number',
  Config_Categories = 'config_categories',
}

export type XAxisSettingType = {
  xAxis?: XAxisValues | string;
  xAxisFormat?: XAxisFormatValues;
  xAxisMax?: number;
  xAxisMin?: number;
  xLogScale?: boolean;
};

export type YAxisSettingType = {
  yLogScale?: boolean;
  yAxisMin?: number;
  yAxisMax?: number;
};

export type GroupSettingType = {
  /** respect the runs table grouping in the chart */
  useRunsTableGroupingInPanels?: boolean;
  groupAgg?: ChartAggOption;
};

export type LinePlotDataSettings = XAxisSettingType &
  YAxisSettingType &
  GroupSettingType & {
    excludeOutliers?: ExcludeOutliersValues;

    // smoothing
    smoothingWeight?: number;
    smoothingType?: SmoothingType;

    /**
     * Workspace setting level uses `maxRuns`, but panel config uses `limit`.
     * This setting tells us maximum number of runs to include for a panel
     */
    maxRuns?: number;
    limit?: number;

    /** Default point vizualization method for runs line plots */
    pointVisualizationMethod?: PointVisualizationOptions;

    /** Show lines before smoothing */
    showOriginalAfterSmoothing?: boolean;
  };

export type LinePlotDisplaySettings = {
  /** Whether run names should be colored in panel tooltips */
  colorRunNames?: boolean;
  /** If a user zoom interaction is synced across panels */
  coordinateZooming?: boolean;
  /** if true, only show the highlighted run in companion plots */
  highlightedCompanionRunOnly?: boolean;
  /** "no crop" option for chart legends on the primary chart to accommodate long run names with crucial information in the middle */
  displayFullRunName?: boolean;
  /** If the x's bucket range is shown in the tooltip on hover */
  showXRange?: boolean;
  /** If the legend is shown  */
  showLegend?: boolean;
  /** Determines how many runs are displayed in panel tooltips */
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions;
};

export type LinePlotSettings = LinePlotDataSettings & LinePlotDisplaySettings;

export type DeprecatedLinePlotSettings = {
  /**@deprecated - use excludeOutliers instead */
  ignoreOutliers?: boolean;
  /**@deprecated - use excludeOutliers instead */
  showMinMaxOnHover?: boolean;
  /**
   * This was previously a workspace level setting. We moved towards showLegend for consistency
   * since that's the value used in the panel config.
   * @deprecated - use showLegend instead
   */
  suppressLegends?: boolean;
};

export type WorkspaceLayoutSettings = {
  autoOrganizePrefix?: OrganizationPrefix;
  /** if false, we'll hide sections with zero panels */
  showEmptySections?: boolean;
  /**
   * whether panels will be auto-generated from logged keys (for backwards
   * compatibility, undefined defaults to true). "pending" means user will
   * be explicitly prompted to select auto or manual mode.
   */
  shouldAutoGeneratePanels?: boolean | 'pending';
  /** sort panels alphabetically */
  sortAlphabetically?: boolean;
};

type SemanticLegendSettings = {
  /** The buckets information for the semantic legend - notably bucketMin and bucketMax */
  buckets?: BucketCustomization[];
  /** The bucket customizations to use for the semantic legend - this is used to customize color and bucketMin/Max for each bucket */
  bucketCustomizations?: Record<number, BucketCustomization>;
  configValuesNumberAggregation?: ConfigValuesNumberAggregation;
  configValuesForRun?: ConfigValueForRun[];
  /** The color to use for the excluded run */
  excludedRunColor?: string;
  /** The key to use for the semantic legend */
  key?: XAxisValues | string;
  /** The metric statistics to use for the current metric - stores min and max for metric min, max, and latest */
  metricStatistics?: Record<
    Exclude<YAxisValues, YAxisValues.Config_Categories>, // Doesn't have min/max/last
    MetricStatistics
  >;
  /** The metric statistics for each run - stores min and max for metric min, max, and latest */
  metricStatisticsForRun?: RunMetricStatistics[];
  /** The number of buckets to use for the semantic legend */
  numberOfBuckets?: number;
  /** Whether to use the original project colors or a semantic legend */
  runColorOptions?: RunColorOptions;
  /** Whether to recalculate the buckets */
  shouldRecalculateBuckets?: boolean | false;
  /** The view ID associated with the semantic legend */
  viewID?: string;
  /** The y-axis value to use for the semantic legend */
  yAxisValue?: YAxisValues;
};

export type SemanticLegendSettingsWithCategoricalConfig =
  SemanticLegendSettings & {
    /** All categories for the selected config key */
    allCategories?: string[];
    /** The categories that were actually selected in the dropdowns */
    selectedCategories?: string[];
    /** The color to use for each run in the selected categories */
    categoryRunColorMap?: Partial<Record<string, string>>;
  };

export type RunMetricStatistics = {
  run_name: string;
  min: number;
  max: number;
  last: number;
};

export type MetricStatistics = {
  min: number;
  max: number;
};

export type BucketCustomization = {
  bucketMin?: number;
  bucketMax?: number;
  color?: string;
};

export enum RunColorOptions {
  OriginalProjectColors = 'default',
  KeyBasedColors = 'semantic-legend',
}

export type SettingSelectOptionType = {
  value: string;
  label: string;
};

export type LinePlotPanel = PanelWithConfig<'Run History Line Plot'> & {
  key?: string | undefined;
} & PanelLayout;

// point aggregation method
export const POINT_VISUALIZATION_OPTIONS = {
  BucketingGorilla: 'bucketing-gorilla',
  Sampling: 'sampling',
  SamplingByDefault: 'sampling-by-default',
} as const;
export type PointVisualizationOptions =
  (typeof POINT_VISUALIZATION_OPTIONS)[keyof typeof POINT_VISUALIZATION_OPTIONS];

export type DerivedPointVisualizationOption = 'bucketing-gorilla' | 'sampling'; // narrow down option to be used in radio UX

export const isPointVizFullFidelity = (
  option: DerivedPointVisualizationOption
) => {
  return option === POINT_VISUALIZATION_OPTIONS.BucketingGorilla;
};

export type ExcludeOutliersValues = 'include-outliers' | 'exclude-outliers';
